import { zodResolver } from "@hookform/resolvers/zod"
import { memo, useMemo, useState } from "react"
import { FaEdit } from "react-icons/fa"

import { useUpdateHousehold } from "@/hooks/api/useHouseholds"
import { usePermissions } from "@/hooks/usePermissions"
import type {
  EditHouseholdProps,
  HouseholdUpdateInput,
} from "@/types/household"
import { householdUpdateSchema } from "@/types/household"
import { Button } from "@chakra-ui/react"

import HouseholdFormDialog from "./HouseholdFormDialog"

const EditHousehold = memo(
  ({ household, onSuccess, onCancel }: EditHouseholdProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const updateHouseholdMutation = useUpdateHousehold()
    const { hasPermission } = usePermissions()

    const defaultValues = useMemo<HouseholdUpdateInput>(
      () => ({
        user_id: household.user_id ?? "",
        parent_first_name: household.parent_first_name ?? "",
        parent_last_name: household.parent_last_name ?? "",
        email: household.email ?? "",
        phone: household.phone ?? "",
        secondary_phone: household.secondary_phone ?? "",
        address: household.address ?? "",
        emergency_contact_name: household.emergency_contact_name ?? "",
        emergency_contact_phone: household.emergency_contact_phone ?? "",
        emergency_contact_relationship:
          household.emergency_contact_relationship ?? "",
        doctor_first_name: household.doctor_first_name ?? "",
        doctor_last_name: household.doctor_last_name ?? "",
        doctor_phone: household.doctor_phone ?? "",
        insurance_provider: household.insurance_provider ?? "",
        referral_source: household.referral_source ?? "",
        agreed_to_policy: household.agreed_to_policy ?? false,
        agreed_to_hold_harmless: household.agreed_to_hold_harmless ?? false,
        policy_agreement_date: household.policy_agreement_date ?? "",
        billing_email: household.billing_email ?? "",
        auto_pay_enabled: household.auto_pay_enabled ?? false,
        notes: household.notes ?? "",
        is_active: household.is_active ?? false,
      }),
      [household],
    )

    const handleClose = () => {
      setIsOpen(false)
      onCancel?.()
    }

    const handleSubmit = async (data: HouseholdUpdateInput) => {
      await updateHouseholdMutation.mutateAsync({
        householdId: household.id,
        householdData: data,
      })
      onSuccess?.()
    }

    // Don't render if user doesn't have permission
    if (!hasPermission("households:update")) {
      return null
    }

    return (
      <>
        <Button
          onClick={() => setIsOpen(true)}
          variant="ghost"
          size="sm"
          aria-label={`Edit household: ${household.id}`}
        >
          <FaEdit fontSize="14px" aria-hidden="true" />
          Edit
        </Button>

        <HouseholdFormDialog
          isOpen={isOpen}
          title="Edit Family"
          submitLabel="Update Family"
          resolver={zodResolver(householdUpdateSchema)}
          defaultValues={defaultValues}
          onSubmit={handleSubmit}
          onClose={handleClose}
          isSubmitting={updateHouseholdMutation.isPending}
          loadingText="Updating..."
        />
      </>
    )
  },
)

EditHousehold.displayName = "EditHousehold"

export default EditHousehold
export type { EditHouseholdProps }
